import React, { useState, useEffect } from 'react';
import { fetchProofTextDefaults } from '../shared/api';

// Typed-in {{token}} values for a true proof.
//
// Text that fits "Joe" often breaks on "Elizabethanne Marie", and a school
// line sized for "Central Elementary" has nowhere to put "Aspire Langston
// Hughes Charter School". The browser canvas is a separate implementation from
// the renderer, so the only trustworthy way to check the extremes is to render
// them — this is where you type them.
//
// A box left alone renders the fixture student's real value, so you edit away
// from the truth rather than from blank.

// Grade is a SELECT, not a text box. The renderer maps a known set of grades
// to display strings and blanks anything else, so a typed "Junior" would come
// back empty and read as a broken look rather than a rejected value. The list
// comes from the server for the same reason — one place to keep in step.
const FIELDS = [
  { token: 'first_name', label: 'First name', placeholder: 'Joe' },
  { token: 'last_name', label: 'Last name', placeholder: 'Smith' },
  { token: 'school_name', label: 'School name', placeholder: 'Central Elementary' },
  { token: 'grade', label: 'Grade', select: true },
  { token: 'grad_year', label: 'Grad year', placeholder: '2027' },
  { token: 'class_name', label: 'Class name', placeholder: 'Class of 2027' },
];

// The two ends worth checking. Deliberately real examples rather than padded
// filler — "Wwwwwwwww" is wider than any name we actually print and would send
// you chasing an overflow that cannot happen.
const PRESETS = {
  shortest: {
    label: 'Shortest',
    values: {
      first_name: 'Jo',
      last_name: 'Ng',
      school_name: 'PS 1',
      grade: '1st Grade',
      grad_year: '2027',
      class_name: 'Class of 2027',
    },
  },
  longest: {
    label: 'Longest',
    values: {
      first_name: 'Elizabethanne Marie',
      last_name: 'Featherstonehaugh-Montgomery',
      school_name: 'Aspire Langston Hughes Charter School',
      grade: 'Transitional Kindergarten',
      grad_year: '2027',
      class_name: 'Transitional Kindergarten Class of 2027',
    },
  },
};

const inputStyle = {
  width: '100%',
  padding: '4px 6px',
  fontSize: 11,
  border: '1px solid #ddd',
  borderRadius: 3,
  background: '#fff',
};

const chipStyle = {
  padding: '3px 8px',
  fontSize: 11,
  borderRadius: 3,
  border: '1px solid #ccc',
  background: '#fff',
  cursor: 'pointer',
};

const ProofTextFields = ({
  season, values, onChange, disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [defaults, setDefaults] = useState({});
  const [gradeOptions, setGradeOptions] = useState([]);

  useEffect(() => {
    if (!season) return;
    fetchProofTextDefaults(season)
      .then((data) => {
        setDefaults(data.defaults || {});
        setGradeOptions(data.grade_options || []);
      })
      // A defaults fetch that fails costs the placeholders, not the feature —
      // every box still types through to the render.
      .catch(() => { setDefaults({}); setGradeOptions([]); });
  }, [season]);

  const set = (token, value) => onChange({ ...values, [token]: value });
  const applyPreset = (key) => onChange({ ...PRESETS[key].values });
  const changedCount = Object.keys(values).length;

  return (
    <div style={{ borderTop: '1px solid #eee' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          textAlign: 'left',
          padding: '6px 16px',
          fontSize: 11,
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          color: '#555',
        }}
      >
        {open ? '▾' : '▸'}
        {' Test text (names, grade, school)'}
        {changedCount > 0 && (
          <span style={{ color: '#1976d2', fontWeight: 600 }}>
            {` — ${changedCount} overridden`}
          </span>
        )}
      </button>

      {open && (
        <div style={{ padding: '0 16px 10px' }}>
          <div style={{
            display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center',
          }}
          >
            {Object.keys(PRESETS).map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => applyPreset(key)}
                disabled={disabled}
                style={chipStyle}
              >
                {PRESETS[key].label}
              </button>
            ))}
            <button
              type="button"
              onClick={() => onChange({})}
              disabled={disabled || changedCount === 0}
              style={{ ...chipStyle, color: changedCount === 0 ? '#bbb' : '#555' }}
            >
              Reset to real values
            </button>
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8,
          }}
          >
            {FIELDS.map((field) => (
              <label key={field.token} style={{ fontSize: 10, color: '#777' }}>
                {field.label}
                {field.select ? (
                  <select
                    value={values[field.token] ?? ''}
                    onChange={(e) => set(field.token, e.target.value)}
                    disabled={disabled}
                    style={inputStyle}
                  >
                    <option value="">{defaults[field.token] || '(the real one)'}</option>
                    {gradeOptions.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={values[field.token] ?? ''}
                    onChange={(e) => set(field.token, e.target.value)}
                    disabled={disabled}
                    placeholder={defaults[field.token] || field.placeholder}
                    style={inputStyle}
                  />
                )}
              </label>
            ))}
          </div>

          <div style={{ fontSize: 10, color: '#999', marginTop: 6 }}>
            Empty box = render the real value. Grade is a list because the renderer
            only knows these — anything else comes back blank.
          </div>
        </div>
      )}
    </div>
  );
};

export default ProofTextFields;
